import ThreeViewObj, { SurfaceMaterial, CameraSettings, ThreeScenePlugin } from "./ThreeView"
import ThreeScene from "./ThreeScene";
import { TemsObject3D } from "@/CommonTypes"
import TemsWebEvents from "@/TemsWebEvents"
import _ from "lodash";
import MouseEvents from "./MouseEvents"

export interface IDragAndDropDirectionStrategy<T extends TemsObject3D> {
    onDrag(selection: ISelectionCandidate<T>): void;
    onDrop(): void;
    getNewCoordinates(direction: THREE.Line3): THREE.Vector3;
}

export default class DragAndDrop<T extends TemsObject3D> implements ThreeScenePlugin {
    private view: ThreeViewObj;
    private scene: ThreeScene;
    private directionStrategy: IDragAndDropDirectionStrategy<T>;
    private mouseEvents: MouseEvents;

    private selectHandler: (selection?: T) => void = () => { };
    private hoverHandler?: (selection?: T) => void = null;
    private doubleClickSelectHandler: (selection?: T) => void = () => { };
    private dropHandler: (selection: T) => void = () => { };
    private moveHandler: (selection: T, oldPos: THREE.Vector3, newPos: THREE.Vector3) => void = () => { };
    
    private movableChecker: (selection: T) => boolean = (a) => true;
    private movingObjects: IMovementEntry<T>[] = [];
    private dragStartPoint = new THREE.Vector3();
        
    constructor(
        view: ThreeViewObj,
        scene: ThreeScene,
        directionStrategy: IDragAndDropDirectionStrategy<T>
    ) {
        this.view = view;
        this.scene = scene;
        this.directionStrategy = directionStrategy;
        this.mouseEvents = new MouseEvents(
            view.renderer.domElement,
            (e) => this.handleClick(e),
            (e) => this.handleDoubleclick(e),
            (e) => this.handleDrag(e),
            (e) => this.handleMove(e),
            (e) => this.handleDrop(e)
        );
        this.mouseEvents.onHover(e => this.handleHover(e));
    }

    unRegister(): void {
        this.selectHandler = this.doubleClickSelectHandler = this.moveHandler = this.dropHandler = this.hoverHandler = null;
        this.mouseEvents.destruct();
    }

    setMovableChecker(selectableChecker: (selection: T) => boolean) {
        this.movableChecker = selectableChecker;
    }

    protected isSelectableMovable(selection: T) {
        return selection.meshInfo && (<any>selection.meshInfo).IsMovable
            && this.movableChecker(selection);
    }

    private handleDoubleclick(event: MouseEvent): void {
        event.preventDefault();
        var selection = this.getSelectionCandidate(event);
        this.doubleClickSelectHandler(selection.selectable);
    }

    private handleClick(event: MouseEvent): void {
        event.preventDefault();
        var selection = this.getSelectionCandidate(event);
        this.selectHandler((selection) ? selection.selectable : null); 
    }

    private handleHover(event: MouseEvent): void {
        if (this.hoverHandler) {
            var selection = this.getSelectionCandidate(event);
            this.hoverHandler((selection) ? selection.selectable : null);
        }
    }

    private handleDrag(event: MouseEvent) {
        var selection = this.getSelectionCandidate(event);

        if (selection) {
            if (this.isSelectableMovable(selection.selectable)) {
                // inform other UI parts about selection
                this.view.getControls().enabled = false; // Disable camera controls
                this.movingObjects.push({ mesh: selection.selectable, startPos: selection.selectable.position.clone() });

                this.dragStartPoint = selection.intersection.point;
                this.directionStrategy.onDrag(selection);
                this.view.$elementScenery[0].style.cursor = 'move';
            } 
        }
    }

    private handleMove(event: MouseEvent) {
        if (this.movingObjects.length > 0) {
            event.preventDefault();
            
            const mouse = this.getMouseDirection(event);
            const vector = new THREE.Vector3(mouse.x, mouse.y, this.view.camera.near).unproject(this.view.camera);
            const direction = new THREE.Vector3().subVectors(vector, this.view.camera.position).normalize();
            const endPoint = new THREE.Vector3().copy(direction).multiplyScalar(10000).add(this.view.camera.position);

            const intersectionPoint = this.directionStrategy.getNewCoordinates(
                new THREE.Line3(this.view.camera.position, endPoint)
            );

            if (typeof intersectionPoint === "undefined") {
                return;
            }

            for (let i = 0; i < this.movingObjects.length; i++) {
                this.moveHandler(
                    this.movingObjects[i].mesh,
                    this.movingObjects[i].startPos.clone(),
                    this.movingObjects[i].startPos.clone().sub(this.dragStartPoint).add(intersectionPoint)
                );
                
                TemsWebEvents.publishEvent(TemsWebEvents.MeshUpdated, [this.movingObjects[i].mesh, "mousemoved"]);
            }

            this.view.renderFrame();
        }
    }

    private handleDrop(e: MouseEvent) {
        e.preventDefault();
        
        this.view.getControls().enabled = true;
        this.view.$elementScenery[0].style.cursor = 'auto';
        this.view.renderFrame();

        if (this.movingObjects) {
            _.each(this.movingObjects, (e) => this.dropHandler(e.mesh));
            this.movingObjects = [];
            this.directionStrategy.onDrop();
        }

    }

    private getSelectionCandidate(event: MouseEvent) {
        var mouse = this.getMouseDirection(event);
        var vector = new THREE.Vector3(mouse.x, mouse.y, 0.5).unproject(this.view.camera);
        var raycaster = new THREE.Raycaster(this.view.camera.position, vector.sub(this.view.camera.position).normalize());

        const selectableMeshes = this.getSelectable(<T[]>this.scene.scene.children);
        const intersects = raycaster.intersectObjects(selectableMeshes, true); // intersects all child objects of the group as well!
        
        if (intersects.length > 0) {
            // Was the hit object actually visible?
            for (var intersectIdx = 0, len = intersects.length; intersectIdx < len; intersectIdx++) {
                var obj = <T>(intersects[intersectIdx].object);
                if (!obj.visible) {
                    continue;
                }
                try {
                    var mesh = this.getClosestSelectable(obj);

                    if (mesh.visible && mesh.meshInfo.IsSelectable) {
                        return {
                            selectable: <T>mesh,
                            intersection: intersects[intersectIdx]
                        };
                    }
                } catch (e) {
                    console.error(e, intersects[intersectIdx].object);
                    throw e;
                }
            }
        }
        return null;
    }    
   
    private getSelectable(m: T[]): T[] {
        return _.reduce(_.map(m, (obj) => {
            if (obj.visible && obj.meshInfo && (!obj.meshInfo.Id || obj.meshInfo.Id.indexOf("-") === -1) && obj.meshInfo.IsSelectable !== false) {
                return [obj];
            }
            if (obj.children) {
                return this.getSelectable(<T[]>obj.children);
            }
            return [];
        }), (a, b) => a.concat(b), []);
    }

    private getClosestSelectable(maybeSelectable: T) {
        if (maybeSelectable.visible && maybeSelectable.meshInfo && maybeSelectable.meshInfo.IsSelectable) {
            return maybeSelectable;
        } else if (maybeSelectable.parent) {
            return this.getClosestSelectable(<T>maybeSelectable.parent);
        }
        throw "There is no Selectable in the hierarchy";
    }

    private getMouseDirection(event: MouseEvent): THREE.Vector2 {
        let divX = event.offsetX;
        let divY = event.offsetY;

        // Ugly work-around for Firefox
        if (event.offsetX === 0 && event.offsetY === 0) {
            divX = event.layerX;
            divY = event.layerY;
        }

        const mouse = new THREE.Vector2();

        mouse.x = (divX / this.view.$elementScenery.innerWidth()) * 2 - 1;
        mouse.y = (-divY / this.view.rendererHeight) * 2 + 1;

        return mouse;
    }

    public onHover(callback: (selection: T) => void) {
        if (this.hoverHandler) { throw "Hover handler already set"; }
        this.hoverHandler = callback;
    }

    public onSelect(callback: (selection: T) => void) {
        var previous = this.selectHandler;
        this.selectHandler = (s) => { previous(s); callback(s); };
    }
    public onDoubleclickSelect(callback: (selection: T) => void) {
        var previous = this.doubleClickSelectHandler;
        this.doubleClickSelectHandler = (s) => { previous(s); callback(s); };
    }
    public onDrop(callback: (selection: T) => void) {
        var previous = this.dropHandler;
        this.dropHandler = (s) => { previous(s); callback(s); };
    }
    public onMove(callback: (selection: T, oldPos: THREE.Vector3, newPos: THREE.Vector3) => void) {
        var previous = this.moveHandler;
        this.moveHandler = (s, oldPos, newPos) => { previous(s, oldPos, newPos); callback(s, oldPos, newPos); };
    }
};

export interface ISelectionCandidate<T extends TemsObject3D> {
    selectable: T;
    intersection: THREE.Intersection;
}
interface IMovementEntry<T extends TemsObject3D> {
    mesh: T,
    startPos: THREE.Vector3
}